import { cn, NoddiIcon } from 'noddi-ui-common';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { NoddiButton } from '../Inputs/NoddiButton';
import { ActionButton, PreviewCard } from './PreviewCard';

interface Props {
  onFileSelect?: (file: File | null) => void;
  onRemove?: () => Promise<unknown> | void;
  onSave?: (file: File) => Promise<unknown> | void;
  saveLabel?: string;
  maxSize?: number;
  accept?: Record<string, string[]>;
  label?: string;
  error?: string;
  currentImageUrl?: string | null;
  readOnly?: boolean;
}

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024;
const DEFAULT_ACCEPT: Record<string, string[]> = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg']
};

export const ImageUpload = ({
  onFileSelect,
  onRemove,
  onSave,
  saveLabel = 'Save',
  maxSize = DEFAULT_MAX_SIZE,
  accept = DEFAULT_ACCEPT,
  label,
  error,
  currentImageUrl,
  readOnly = false
}: Props) => {
  const [preview, setPreview] = useState<{ url: string; name: string; size: number; file: File } | null>(null);
  // The url this component removed, not a flag: a later url is a different image and shows on its
  // own, so nothing has to reset this when the parent re-fetches.
  const [removedImageUrl, setRemovedImageUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    return () => {
      if (preview?.url) {
        URL.revokeObjectURL(preview.url);
      }
    };
  }, [preview?.url]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }
      if (preview?.url) {
        URL.revokeObjectURL(preview.url);
      }
      setPreview({ url: URL.createObjectURL(file), name: file.name, size: file.size, file });
      onFileSelect?.(file);
    },
    accept,
    maxFiles: 1,
    maxSize,
    multiple: false,
    noClick: true,
    noKeyboard: true
  });

  const handleCancelPreview = () => {
    if (preview?.url) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
    onFileSelect?.(null);
  };

  const handleRemoveCurrent = async () => {
    if (!onRemove) {
      return;
    }
    setIsRemoving(true);
    try {
      await onRemove();
      setRemovedImageUrl(currentImageUrl ?? null);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSave = async () => {
    if (!preview || !onSave) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave(preview.file);
      URL.revokeObjectURL(preview.url);
      setPreview(null);
      onFileSelect?.(null);
    } finally {
      setIsSaving(false);
    }
  };

  const showSaved = !!currentImageUrl && !preview && currentImageUrl !== removedImageUrl;
  const showDirty = !!preview;
  const showDropzone = !readOnly && !showSaved && !showDirty;

  if (readOnly && currentImageUrl) {
    return (
      <PreviewCard
        key={currentImageUrl}
        src={currentImageUrl}
        name={filenameFromUrl(currentImageUrl)}
        tone='default'
        readOnly
      />
    );
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <input {...getInputProps()} aria-label={label ?? 'Upload image'} />
      {showSaved && (
        <PreviewCard
          key={currentImageUrl}
          src={currentImageUrl}
          name={filenameFromUrl(currentImageUrl)}
          onReplace={open}
          tone={error ? 'error' : 'default'}
          secondary={
            onRemove ? (
              <ActionButton
                tone='danger'
                icon='Trash2'
                // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleRemoveCurrent(); React discards the returned promise, so a rejection is unhandled unless the body catches
                onClick={handleRemoveCurrent}
                disabled={isRemoving}
                loading={isRemoving}
              >
                Remove
              </ActionButton>
            ) : null
          }
        />
      )}

      {showDirty && (
        <PreviewCard
          key={preview.url}
          src={preview.url}
          name={preview.name}
          size={preview.size}
          onReplace={open}
          // Without onSave the owning form persists the file on submit, so there is nothing separately unsaved.
          tone={error ? 'error' : onSave ? 'dirty' : 'default'}
          secondary={
            <ActionButton tone='muted' icon='X' onClick={handleCancelPreview} disabled={isSaving}>
              Cancel
            </ActionButton>
          }
          primary={
            onSave ? (
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleSave(); React discards the returned promise, so a rejection is unhandled unless the body catches
              <NoddiButton size='extraSmall' onClick={handleSave} loading={isSaving}>
                {saveLabel}
              </NoddiButton>
            ) : null
          }
        />
      )}

      {showDropzone && (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- react-dropzone root; the dropzone runs with noClick/noKeyboard so this onClick is the only opener, which makes it mouse-only - tracked as a follow-up
        <div
          {...getRootProps()}
          onClick={open}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-6 transition-colors',
            isDragActive
              ? 'border-darkPurple bg-lightPurple/40'
              : error
                ? 'border-danger bg-danger50/30'
                : 'border-silverGray bg-darkPurpleBg hover:bg-darkPurpleGrey'
          )}
        >
          <div className='flex size-10 items-center justify-center rounded-full border border-border bg-background text-grey'>
            <NoddiIcon name='Upload' size='small' />
          </div>
          <div className='text-sm font-medium text-foreground'>
            {label ?? (
              <>
                Drag and drop, or <span className='text-darkPurple underline underline-offset-2'>browse</span>
              </>
            )}
          </div>
          <div className='text-xs text-muted-foreground'>PNG, JPG or SVG · up to {formatMaxSize(maxSize)}</div>
        </div>
      )}

      {error && (
        <div className='flex items-center gap-1.5 text-xs text-dangerDark'>
          <NoddiIcon name='TriangleAlert' size='xSmall' />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

const filenameFromUrl = (url: string): string => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const last = path.split('/').pop() ?? '';
    return decodeURIComponent(last) || 'Image';
  } catch {
    return 'Image';
  }
};

const formatMaxSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb % 1 === 0 ? mb : mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};
